import { sumBy } from 'lodash';
import { WithdrawalStatus } from 'further-types/withdrawal';
import {
  numberToCurrencyString,
  dateToLabel,
  DateRange,
  getDateRange,
  downloadFile,
} from 'further-ui/utils';
import {
  RequestSourceToLabelMap,
  StatusToLabelMap,
} from 'adminConstants/withdrawalProperties';
import Table, { FooterCell, FooterRow, TableActions } from 'components/Table';
import PageContainer from 'components/PageContainer';
import { Close, Delete, Undo } from '@mui/icons-material';
import FiltersAndActions from './FiltersAndActions';
import NotesAndAttachmentColumn from './NotesAndAttachmentColumn';

import {
  useExportWithdrawals,
  useGetWithdrawals,
  useRejectWithdrawal,
  useRevertWithdrawal,
} from 'hooks/data/withdrawal/useWithdrawal';
import CustomDeleteWithdrawalModal from './CustomDeleteWithdrawalModal';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import PageContent from 'components/PageContent';
import { useDebounce } from 'usehooks-ts';
import { styled } from '@mui/material';
import trpcApi, { ApiResponses } from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Withdrawal = ReturnType<
  typeof useGetWithdrawals
  //@ts-expect-error
>['data']['withdrawals'][number];

const StyledNoWrapSpan = styled('span')({
  whiteSpace: 'nowrap',
});

const ReviewWithdrawals: React.FC = () => {
  const [canManageAndExportWithdrawals] = useHasPermissions([
    RolePermission.ExportCustodianWithdrawals,
  ]);

  const pagination = usePagination({
    id: 'withdrawal-requests-table',
    orderBy: 'createdAt',
    order: 'desc',
  });
  const { filters, handleChangeFilters } = useFilters<{
    dateRange?: DateRange;
    investorName: string;
    status: WithdrawalStatus;
  }>('withdrawal-requests-table', {
    defaultFilters: {
      investorName: '',
      dateRange: undefined,
      status: WithdrawalStatus.Requested,
    },
    onFiltersChange: pagination.toFirstPage,
  });
  const rejectWithdrawal = useRejectWithdrawal();
  const revertWithdrawal = useRevertWithdrawal();
  const deleteModalDisclosure = useDisclosure<{ withdrawalId: string }>();
  const debouncedInvestorName = useDebounce(filters.investorName, 250);
  //@ts-expect-error
  const withdrawals = useGetWithdrawals({
    ...pagination.properties,
    ...filters,
    investorName: debouncedInvestorName,
    ...getDateRange(filters.dateRange),
  });
  //@ts-expect-error
  const exportWithdrawals = useExportWithdrawals({
    ...pagination.properties,
    ...filters,
    investorName: debouncedInvestorName,
    ...getDateRange(filters.dateRange),
  });

  const handleExport = (sendToCustodian?: boolean) => {
    exportWithdrawals.mutate(!!sendToCustodian);
  };

  const columns = [
    {
      label: 'Investor Name',
      key: 'investorName',
      render: (elm: Withdrawal) => elm.investor.fullName,
    },
    {
      label: 'Net withdrawal amount',
      key: 'amount',
      render: (elm: Withdrawal) => numberToCurrencyString(elm?.amount),
    },
    {
      label: 'Withdrawal fee',
      key: 'withdrawalFee',
      render: (elm: Withdrawal) =>
        numberToCurrencyString(elm?.withdrawalFee || 0),
    },
    {
      label: 'Withdrawal request date',
      key: 'withdrawalDate',
      render: (elm: Withdrawal) => dateToLabel(elm?.withdrawalDate),
    },
    {
      label: 'Withdrawal type',
      key: 'requestSource',
      render: (elm: Withdrawal) => RequestSourceToLabelMap[elm?.requestSource],
    },
    {
      label: 'Name on bank account',
      key: 'nameOnBankAccount',
      render: (elm: Withdrawal) => elm?.investorBankDetails?.bankAccountName,
      sort: false,
    },
    {
      label: 'Bank details',
      key: 'bankDetails',
      render: (elm) => {
        const accNo = elm?.investorBankDetails?.bankAccountNumber;
        const sc = elm?.investorBankDetails?.bankSortCode;
        const iban = elm?.investorBankDetails?.iban;
        if (accNo && sc) return `${accNo} ${sc}`;
        if (iban) return iban;
        return 'No bank details found';
      },
      sort: false,
    },
    {
      label: 'Status',
      key: 'status',
      render: (elm: Withdrawal) => (
        <StyledNoWrapSpan>{StatusToLabelMap[elm?.status]}</StyledNoWrapSpan>
      ),
      sort: false,
    },
    {
      label: 'Notes',
      key: 'notes',
      render: (elm: Withdrawal) => <NotesAndAttachmentColumn {...elm} />,
      sort: false,
    },
    {
      label: 'Actions',
      key: 'actions',
      render: (elm: Withdrawal) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              label: 'Undo',
              onClick: () => revertWithdrawal.mutate({ withdrawalId: elm._id }),
              icon: Undo,
              loading: revertWithdrawal.isPending,
              visible:
                elm.status === WithdrawalStatus.Rejected &&
                canManageAndExportWithdrawals,
            },
            {
              label: 'Reject',
              onClick: () => {
                // If this is a bulk upload, show the delete modal instead of rejecting directly
                if (elm.bulkWithdrawalUploadId) {
                  deleteModalDisclosure.stageAction({
                    withdrawalId: elm._id,
                  });
                } else {
                  rejectWithdrawal.mutate(elm._id);
                }
              },
              icon: Close,
              color: 'error',
              loading: rejectWithdrawal.isPending,
              visible:
                filters.status === WithdrawalStatus.Requested &&
                canManageAndExportWithdrawals,
            },
            {
              label: 'Undo custodian export',
              visible:
                (elm.status === WithdrawalStatus.ExportedToCustodian ||
                  elm.status === WithdrawalStatus.BulkUploaded) &&
                canManageAndExportWithdrawals,
              onClick: () => revertWithdrawal.mutate({ withdrawalId: elm._id }),
              icon: Undo,
              loading: revertWithdrawal.isPending,
            },
            {
              label: 'Undo and reject withdrawal',
              visible:
                (elm.status === WithdrawalStatus.ExportedToCustodian ||
                  elm.status === WithdrawalStatus.BulkUploaded) &&
                canManageAndExportWithdrawals,
              onClick: () =>
                revertWithdrawal.mutate({
                  withdrawalId: elm._id,
                  shouldReject: true,
                }),
              icon: Close,
              color: 'error',
              loading: revertWithdrawal.isPending,
            },
            {
              label: 'Delete withdrawal record',
              // Only show in BulkUploaded filter view, or for exported bulk withdrawals
              // In Requested filter, non-exported bulk withdrawals should only show "Reject"
              visible:
                !!elm.bulkWithdrawalUploadId &&
                (filters.status === WithdrawalStatus.BulkUploaded ||
                  elm.status === WithdrawalStatus.ExportedToCustodian ||
                  elm.status === WithdrawalStatus.BulkUploaded),
              onClick: () => {
                deleteModalDisclosure.stageAction({
                  withdrawalId: elm._id,
                });
              },
              icon: Delete,
              color: 'error',
            },
          ]}
        />
      ),
      sort: false,
    },
  ];
  const tableEmtpyMessage = [
    WithdrawalStatus.ExportedToCustodian,
    WithdrawalStatus.Rejected,
  ].includes(filters.status)
    ? `No withdrawal requests found that have been ${StatusToLabelMap[
        filters.status
      ].toLowerCase()}`
    : 'No withdrawal requests found';

  const withdrawalsList = withdrawals.data?.withdrawals || [];

  const { mutate: deleteWithdrawal, isPending: isDeletingWithdrawal } =
    trpcApi.withdrawals.deleteWithdrawal.useMutation(
      useHandleTrpcMutation()({
        onSuccess: () => {
          deleteModalDisclosure.onClose();
        },
      }),
    );

  const { mutate: deleteBulkUpload, isPending: isDeletingBulkUpload } =
    trpcApi.withdrawals.deleteBulkUpload.useMutation(
      useHandleTrpcMutation()({
        onSuccess: () => {
          deleteModalDisclosure.onClose();
        },
      }),
    );

  const {
    mutate: downloadBulkUploadFile,
    isPending: isDownloadingBulkUploadFile,
  } = trpcApi.withdrawals.downloadBulkUploadFile.useMutation(
    useHandleTrpcMutation<
      ApiResponses['withdrawals']['downloadBulkUploadFile']
    >()({
      onSuccess: (data) => {
        const byteCharacters = atob(data.fileBase64);
        const byteNumbers = new Array(byteCharacters.length)
          .fill(0)
          .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        downloadFile(blob, data.filename || 'withdrawals.xlsx');
      },
    }),
  );

  return (
    <PageContainer heading="Withdrawals: Withdrawal record">
      <PageContent>
        <FiltersAndActions
          filters={filters}
          handleChangeFilters={handleChangeFilters}
          resultCount={withdrawalsList.length}
          hasExportError={exportWithdrawals.isError}
          isExportLoading={exportWithdrawals.isPending}
          onExport={handleExport}
        />
        <Table
          columns={columns}
          tablebody={withdrawalsList}
          count={withdrawals.data?.totalCount || 0}
          orderBy={pagination.orderBy}
          order={pagination.order}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          onRequestSort={pagination.handleRequestSort}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          pagination={true}
          loading={withdrawals.isFetching}
          emptyMessage={tableEmtpyMessage}
          variant="nohover"
          TableFooter={
            <FooterRow>
              <FooterCell>Total</FooterCell>
              <FooterCell>
                {numberToCurrencyString(sumBy(withdrawalsList, 'amount'))}
              </FooterCell>
            </FooterRow>
          }
        />
        <CustomDeleteWithdrawalModal
          isOpen={deleteModalDisclosure.isOpen}
          onClose={deleteModalDisclosure.onClose}
          onDeleteSingle={() => {
            const id = deleteModalDisclosure.actionProps?.withdrawalId;
            if (id) {
              deleteWithdrawal({ withdrawalId: id });
            }
          }}
          onDeleteBulk={() => {
            const id = deleteModalDisclosure.actionProps?.withdrawalId;
            if (id) {
              deleteBulkUpload({ withdrawalId: id });
            }
          }}
          onDownloadFile={() => {
            const id = deleteModalDisclosure.actionProps?.withdrawalId;
            if (id) {
              downloadBulkUploadFile({ withdrawalId: id });
            }
          }}
          isDeleting={isDeletingWithdrawal || isDeletingBulkUpload}
          isDownloadingBulkUploadFile={isDownloadingBulkUploadFile}
        />
      </PageContent>
    </PageContainer>
  );
};

export default ReviewWithdrawals;
